'use client';

import { useEffect, useRef } from 'react';
import { track } from '@upms/analytics';
import { isSessionValid, isStripeSession, type PaymentSession } from '@/lib/api/billing';
import { usePolicy } from '@/hooks/usePolicy';
import { POLICY_EVENTS, type PaymentProvider } from '@/lib/analytics/events';

export type PaymentDialogPhase =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'ready'; provider: PaymentProvider; policyNumber: string };

// TanStack's own discriminated union, not a hand-written `{ status: ...; data:
// ... | undefined }` shape: that flattened form would decouple `data`'s
// definedness from `status` (a query can still hold stale `data` while
// `status` is `'error'` mid-refetch) and stop `policyQuery.status ===
// 'success'` from narrowing it below.
type PolicyQuery = ReturnType<typeof usePolicy>;

type PhaseInput = {
  policyQuery: PolicyQuery;
  session: PaymentSession | undefined;
  isSessionFetching: boolean;
  sessionIsError: boolean;
};

/**
 * Which branch PaymentDialogContent renders, and the provider + policy number
 * once known. Pure — no hooks — so it's directly unit-testable, and it's the
 * one place `policyQuery.data` is narrowed: TypeScript only narrows it
 * through `policyQuery.status` checked in this same function scope, not
 * through a derived boolean read back out of it.
 *
 * Deliberately does not return the narrowed `session` itself: TypeScript's
 * discriminant narrowing of `session` (excluding the sibling `stripe`/`zuora`
 * field and `undefined`) only holds for a plain local variable checked
 * in-scope, not for a value round-tripped through an object property — so
 * PaymentDialogContent re-establishes it with its own `isSessionValid` check.
 */
export const getPaymentDialogPhase = ({
  policyQuery,
  session,
  isSessionFetching,
  sessionIsError,
}: PhaseInput): PaymentDialogPhase => {
  // Initial fetch: policy pending, or the session is still resolving (platform
  // unknown) — show common loading, not an ErrorBody flash or a Zuora default.
  // A method switch keeps a valid `session` (keepPreviousData), so it skips this
  // and stays in its platform branch with scoped fields loading.
  if (policyQuery.status === 'pending' || (session === undefined && isSessionFetching)) {
    return { kind: 'loading' };
  }

  if (policyQuery.status === 'error' || sessionIsError || !isSessionValid(session)) {
    return { kind: 'error' };
  }

  return {
    kind: 'ready',
    provider: isStripeSession(session) ? 'stripe' : 'zuora',
    policyNumber: policyQuery.data.policyNumber,
  };
};

/**
 * Fires `Payment Method Update Started` exactly once per dialog open. Held
 * until the phase resolves, because `provider` is unknown until the session
 * does — and AC#2 needs conversion segmented by provider. Fires on the error
 * branch too (provider: 'unknown'), so no flow drops out of the funnel.
 */
export const useTrackPaymentMethodUpdateStarted = (phase: PaymentDialogPhase): void => {
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current || phase.kind === 'loading') return;

    startedRef.current = true;
    const provider = phase.kind === 'ready' ? phase.provider : 'unknown';
    track(POLICY_EVENTS.paymentMethodUpdateStarted, { provider });
  }, [phase]);
};
